<script setup>
import { onMounted, ref } from 'vue';
import {useRoute, useRouter} from 'vue-router';
import { getPublicFundraiser } from "@/services/api_services";

const route = useRoute();
const fundraiserId = route.params.id;

const fundraiser = ref(null);
const loading = ref(true);
const error = ref(null);

const router = useRouter();

function goToMpesaContributionPage(fundraiserId,fundraiserName,fundraiserIdentifier) {
  router.push({
    name: 'MpesaContribution',
    params: { fundraiserId: fundraiserId, fundraiserName: fundraiserName, fundraiserIdentifier: fundraiserIdentifier},
  })}
function goToPayStackContributionPage(fundraiserId,fundraiserName,fundraiserIdentifier) {
  router.push({ name: 'PayStackContribution',    params: { fundraiserId: fundraiserId, fundraiserName: fundraiserName, fundraiserIdentifier: fundraiserIdentifier} });
}

function goToPledgePage(fundraiserId, fundraiserName, fundraiserIdentifier) {
  router.push({
    name: 'MakePledge',
    params: { fundraiserId: fundraiserId, fundraiserName: fundraiserName, fundraiserIdentifier: fundraiserIdentifier },
  });
}

onMounted(async () => {
  try {
    fundraiser.value = await getPublicFundraiser(fundraiserId);
  } catch (e) {
    console.error(e);
    error.value = "Failed to load fundraiser details. Please try again later.";
  } finally {
    loading.value = false;
  }
});
</script>

<template>
  <div class="fundraiser-container">
    <!-- Loading state -->
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Loading fundraiser details...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="error-state">
      <div class="error-icon">!</div>
      <p>{{ error }}</p>
      <button @click="window.location.reload()" class="retry-button">Retry</button>
    </div>

    <!-- Success state -->
    <div v-else-if="fundraiser" class="fundraiser-details">
      <!-- Main content -->
      <div class="fundraiser-content">
        <!-- Image section -->
        <div class="image-section">
          <img
              :src="fundraiser.images"
              :alt="`Image for ${fundraiser.name}`"
              class="fundraiser-image"
              @error="fundraiser.images = '/placeholder-image.jpg'"
          />
        </div>

        <!-- Description section -->
        <div class="description-section">
          <h2 class="section-title">{{fundraiser.name}}</h2>
          <p class="short-description">{{ fundraiser.shortDescription }}</p>
          <p class="fundraiser-community">By {{ fundraiser.communityName }}</p>
        </div>

        <div class="action-buttons">
          <button @click="
          goToMpesaContributionPage(fundraiser.id,fundraiser.name,fundraiser.identifier)" class="donate-button">Give now (MPESA)</button>
          <button
              v-if="fundraiser.allowsDirectWithdrawals === undefined
        || fundraiser.allowsDirectWithdrawals === null
        || fundraiser.allowsDirectWithdrawals === true"
              @click="goToPayStackContributionPage(fundraiser.id, fundraiser.name, fundraiser.identifier)"
              class="donate-button"
          >
            Give now (Other Payment Methods)
          </button>
          <button
              @click="goToPledgePage(fundraiser.id, fundraiser.name, fundraiser.identifier)"
              class="donate-button"
          >
            Make a Pledge
          </button>
        </div>

        <!-- Footer -->
        <div class="givva-footer">
          <a
              href="https://play.google.com/store/apps/details?id=com.GIVVA.givva&pcampaignid=web_share"
              target="_blank"
              rel="noopener noreferrer"
              class="givva-footer-link"
          >
            © 2026 Givva Wealthtech Limited
          </a>
        </div>

      </div>
    </div>
  </div>
</template>

<style scoped>
.givva-footer {
  margin-top: 1.5rem;
  text-align: center;
  padding: 1rem 0;
}

.givva-footer-link {
  font-size: 13px;
  color: black;
  text-decoration: underline;
  transition: color 0.3s ease;
}

.givva-footer-link:hover {
  color: #14b8a6;
}
.fundraiser-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  color: #1f2937;
  min-height: 100vh;
  background: linear-gradient(
      135deg,
      rgba(203, 217, 196, 1) 0%,
      rgba(249, 247, 194, 1) 50%,
      rgba(249, 213, 206, 1) 100%
  );
}

.loading-state, .error-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  text-align: center;
  border-radius: 12px;
  padding: 2rem;
}

.action-buttons-wrapper {
  margin: 0 -2rem;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #14b8a6;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.error-icon {
  background-color: #ef4444;
  color: white;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 1rem;
  font-weight: bold;
}

.retry-button {
  background-color: #14b8a6;
  color: white;
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-top: 1rem;
}

.retry-button:hover {
  background-color: #14b8a6;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.fundraiser-details {
  border-radius: 16px;
  overflow: hidden;
  padding: 2.5rem;
  margin-top: 1rem;
}

.fundraiser-header {
  text-align: center;
  margin-bottom: 2.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.fundraiser-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  color: #000000;
  line-height: 1.2;
}

.fundraiser-identifier {
  font-size: 0.95rem;
  color: #6b7280;
  letter-spacing: 0.5px;
}



.fundraiser-community {
  font-size: 12px;
  color: #14b8a6;
  font-weight: 500;
  margin-top: 0.5rem;
}

.fundraiser-content {
  display: grid;
  grid-template-columns: 1fr;
  gap: 2.5rem;
}

.image-section {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
}

.image-section:hover {
  transform: translateY(-2px);
}

.fundraiser-image {
  width: 100%;
  max-height: 500px;
  object-fit: cover;
  display: block;
  border-radius: 12px;
}

.section-title {
  font-size: 1.5rem;
  margin-bottom: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  position: relative;
  padding-bottom: 0.75rem;
}

.section-title::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 60px;
  height: 3px;
  background: linear-gradient(to right, #000000, #000000);
  border-radius: 3px;
}

.short-description {
  font-size: 1.15rem;
  line-height: 1.75;
  color: #374151;
}

.long-description {
  font-size: 1rem;
  color: #4b5563;
  line-height: 1.8;
}

.no-description {
  font-style: italic;
  color: #9ca3af;
}

.action-buttons {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;
}

.donate-button,
.pledge-button {
  flex: 1;
  min-width: 200px;
  max-width: 100%;
  padding: 0.85rem 2rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  transition: all 0.3s ease;
  cursor: pointer;
  text-align: center;
  letter-spacing: 0.5px;
  border: none;
}

.donate-button {
  background: linear-gradient(to right, #14b8a6, #14b8a6);
  color: white;
  box-shadow: 0 4px 6px rgba(59, 130, 246, 0.25);
}

.donate-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(59, 130, 246, 0.3);
}

/* Pledge button — outlined teal style to visually distinguish from donate */
.pledge-button {
  background-color: transparent;
  color: #14b8a6;
  border: 2px solid #14b8a6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.pledge-button:hover {
  background-color: rgba(20, 184, 166, 0.08);
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(20, 184, 166, 0.2);
}

@media (min-width: 992px) {
  .action-buttons {
    gap: 2rem;
  }

  .donate-button,
  .pledge-button {
    flex: 0 0 calc(50% - 1rem);
    max-width: calc(50% - 1rem);
  }
}

@media (max-width: 768px) {
  .fundraiser-container {
    padding: 1rem;
  }

  .fundraiser-details {
    padding: 1.5rem;
  }

  .fundraiser-title {
    font-size: 2rem;
  }

  .fundraiser-image {
    max-height: 350px;
  }

  .action-buttons {
    flex-direction: column;
    gap: 1rem;
  }

  .donate-button,
  .pledge-button {
    width: 100%;
    margin-bottom: 10px;
  }
}

@media (max-width: 992px) {
  .fundraiser-image {
    max-height: 400px;
  }
}

.section-title {
  font-size: 1.3rem;
  margin-bottom: 1rem;
}

.short-description {
  font-size: 1rem;
}

@media (max-width: 480px) {
  .fundraiser-image {
    max-height: 250px;
  }

  .fundraiser-title {
    font-size: 1.6rem;
  }

  .donate-button,
  .pledge-button {
    padding: 0.75rem 1.5rem;
    font-size: 0.95rem;
  }
}

@media (max-width: 360px) {
  .fundraiser-image {
    max-height: 200px;
  }

  .fundraiser-title {
    font-size: 1.4rem;
  }
}
</style>